// Zentrale Navigations-Liste des Admin-Bereichs (Roadmap 1.21, Kontrollzentrum Schritt 2, Teil 1).
//
// EINE Liste für Seitenleiste, Seitenschutz (RouteGuard) und Rechte. Jede Gruppe und jede
// Seite hat einen eigenen Schlüssel in page_access. Rechte-Regel je Rolle (src/lib/adminAccess.ts):
//   eigene Zeile der Seite → zählt, sonst die Zeile der Gruppe (Vererbung).
// Neue Seite = Eintrag hier unter ihrer Gruppe. Ohne eigene Zeile erbt sie automatisch:
// wer die Gruppe sieht, sieht auch die neue Seite.
//
// Felder:
//   key          page_access-Schlüssel (Gruppen group.*, Seiten page.* oder ein bestehender admin.*-Schlüssel)
//   path         Link in der Leiste
//   routes       Routen relativ zu /admin, die diese Seite schützt (Standard: aus path abgeleitet).
//                Leeres Array = Seite schützt keine Route (z. B. ungeschützte Startseiten).
//   hidden       nicht in der Leiste (nur Seitenschutz), z. B. Detailseiten oder Altbestand
//   flat         Gruppe wird in der Leiste nicht als Gruppe, sondern als einzelne Einträge oben gezeigt
//   rechteWie    Verknüpfung: zweiter Link auf eine Seite, die schon in einer anderen Gruppe steht. Hat
//                KEINE eigenen Rechte (taucht in „Personen & Rechte" nicht auf, schützt keine Route) und
//                ist genau dann sichtbar, wenn die Zielseite in ihrer Gruppe sichtbar ist.
//   bereiche     Bereiche INNERHALB einer Seite (CO-8, z. B. Customer Support). Eigene page_access-Schlüssel
//                unter dem Seitenschlüssel, Vererbung: Unterbereich → Bereich → Seite → Gruppe.
//                Steuern nur, was man auf der Seite sieht, nicht die Route. hinweis = Text in „Personen &
//                Rechte“, z. B. wenn die Datenbank die Daten trotzdem nur Founder/Admin gibt.
// Seit Teil 3 (04.10.2026) gibt es keine fest eingetragenen Walker-Freigaben mehr: auch Walker sehen
// genau das, was page_access (mit Vererbung) erlaubt.
import type { ComponentType } from "react";
import {
  Activity, BarChart3, Bot, Brain, Briefcase, Building2, CalendarDays, Camera, ClipboardCheck, ClipboardList, Clock, CreditCard, Dog,
  Euro, FileEdit, Gauge, Gift, Globe, HeadphonesIcon, Home, Inbox, ListChecks, MapPinned, MessageCircle,
  Navigation, Newspaper, PartyPopper, PawPrint, Rocket, Scissors, Settings, Shield, SlidersHorizontal, Sparkles, Star, TreePine,
  UserCheck, UserCog, Users, XCircle, Archive,
} from "lucide-react";

export type NavBadgeKey = "ai_pending" | "schnuppertag_new" | "walker_apps_new" | "schicht_aenderungen";

export interface AdminNavPage {
  key: string;
  label: string;
  path: string;
  icon: ComponentType<{ className?: string }>;
  routes?: string[];
  hidden?: boolean;
  badgeKey?: NavBadgeKey;
  rechteWie?: { key: string; group: string };
  bereiche?: AdminNavBereich[];
}

export interface AdminNavBereich {
  key: string;
  label: string;
  hinweis?: string;
  unter?: { key: string; label: string; hinweis?: string }[];
}

const NUR_FOUNDER_ADMIN_DATEN = "Daten liefert die Datenbank heute nur Founder und Admin – für andere bleibt es leer.";

export interface AdminNavGroup {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  pages: AdminNavPage[];
  flat?: boolean;
  hidden?: boolean;
}

export const ADMIN_NAV: AdminNavGroup[] = [
  {
    // Neu (Noah 02.10.2026): eigene Gruppe, nur Founder und Admin.
    key: "group.brain",
    label: "Brain",
    icon: Brain,
    pages: [
      // /admin ist bewusst ohne Seitenschutz (Einstiegsseite, siehe App.tsx).
      { key: "page.company_brain", label: "Company Brain", path: "/admin", icon: Brain, routes: [] },
      { key: "page.brain_input", label: "Brain Input", path: "/admin/brain-input", icon: FileEdit },
      // Freisteller-Audit „helle Hunde“ (15.10.2026): erbt group.brain = nur Founder und Admin.
      // Seit 19.10.2026 nicht mehr in der Leiste (Noah): Einstieg über Avatare → „Freisteller im Detail“.
      { key: "page.freisteller_pruefung", label: "Freisteller prüfen", path: "/admin/freisteller-pruefung", icon: Scissors, hidden: true },
    ],
  },
  {
    // Mein Tag bleibt ein einzelner Eintrag oben (Walker behalten ihn).
    key: "group.start",
    label: "Mein Tag",
    icon: Home,
    flat: true,
    pages: [
      // /admin/dogwalker ist bewusst ohne Seitenschutz (wie bisher).
      { key: "page.mein_tag", label: "Mein Tag", path: "/admin/dogwalker", icon: Home, routes: [] },
    ],
  },
  {
    key: "group.gassi",
    label: "Gassi-Service",
    icon: TreePine,
    pages: [
      { key: "page.regeltermin", label: "Regeltermin", path: "/admin/regeltermin", icon: TreePine },
      // /walker liegt außerhalb von /admin und hat keinen Seitenschutz.
      { key: "page.gassi_fotos", label: "Gassi-Fotos", path: "/walker", icon: Camera, routes: [] },
      { key: "page.walk_photo_audit", label: "Foto-Audit", path: "/admin/walk-photo-audit", icon: Camera },
    ],
  },
  {
    key: "group.daycare",
    label: "Daycare",
    icon: PawPrint,
    pages: [
      // „Regeltermin“ statt „Übersicht“ (Noah 20.10.2026). Gleicher Name wie die Gassi-Seite, andere Gruppe.
      { key: "page.daycare", label: "Regeltermin", path: "/admin/daycare", icon: CalendarDays },
      { key: "page.daycare_checkin", label: "Check-in", path: "/admin/daycare/checkin", icon: UserCheck },
      { key: "page.daycare_aufgaben", label: "Aufgaben", path: "/admin/daycare/aufgaben", icon: ListChecks },
      // Administrative ToDos (Impfpass-Erneuerung 1.41, 08.10.2026): erbt die Rechte von group.daycare.
      { key: "page.daycare_todos", label: "Administrative ToDos", path: "/admin/daycare/todos", icon: ClipboardCheck },
      { key: "page.daycare_gruppen", label: "Gruppenmanagement", path: "/admin/daycare/gruppen", icon: MapPinned },
      { key: "page.daycare_kapazitaet", label: "Kapazität", path: "/admin/daycare/kapazitaet", icon: Gauge },
      { key: "page.daycare_postfach", label: "HuTa Postfach", path: "/admin/daycare/postfach", icon: Inbox },
      // CharliesCam CC-5 „Kamera-Bilder“ (13.10.2026, von Noah freigegeben): NICHT in der Leiste (hidden), der
      // Eintrag ist nur der Seitenschutz. Eigene page_access-Zeilen (keine Vererbung von group.daycare):
      // founder/admin/daycare_manager/supervisor an (supervisor seit 15.10.2026, Entscheidung N2),
      // city_manager/walker aus. Zusätzlich hinter dem
      // App-Schalter huta_kamera_bilder (Standard aus). `hidden` erst mit echten Bildern entfernen (zweites OK).
      { key: "page.daycare_kamera", label: "Kamera-Bilder", path: "/admin/daycare/kamera", icon: Camera, hidden: true },
      // Die Route der Abo-Statistik war schon immer über Daycare geschützt, der Link steht aber unter Stats.
      // Bis Noah das bei der Rechte-Oberfläche entscheidet: Route hier, Link unter Stats (gleiches Verhalten wie bisher).
      { key: "page.daycare_abo_stats_route", label: "Daycare-Abos (Route)", path: "/admin/stats/daycare-abos", icon: PawPrint, hidden: true },
      // Kein Link auf die Abo-Verwaltung in der Daycare-Gruppe (Noah 14.10.2026: „soll weg da“).
      // Die Seite /admin/daycare/abos steht nur noch unter Stats (page.daycare_abos).
    ],
  },
  {
    key: "group.funnel",
    label: "Upper Funnel",
    icon: Rocket,
    pages: [
      { key: "page.website_traffic", label: "Website-Traffic", path: "/admin/stats/website-traffic", icon: Globe },
      { key: "page.ai_operator", label: "AI Operator", path: "/admin/marketing/ai-operator", icon: Bot, badgeKey: "ai_pending" },
      { key: "page.schnuppertag_stats", label: "Schnuppertag-Statistik", path: "/admin/schnuppertag-stats", icon: BarChart3, hidden: true },
    ],
  },
  {
    key: "group.gassi_funnel",
    label: "Gassi Funnel",
    icon: PawPrint,
    pages: [
      { key: "page.gassi_funnel", label: "Funnel-Board", path: "/admin/gassi-funnel", icon: Rocket },
      { key: "page.schnuppertag", label: "Schnuppertag", path: "/admin/schnuppertag", icon: PawPrint, badgeKey: "schnuppertag_new" },
    ],
  },
  {
    key: "group.daycare_funnel",
    label: "Daycare Funnel",
    icon: PawPrint,
    pages: [
      { key: "page.daycare_funnel", label: "Funnel", path: "/admin/funnel", icon: Rocket },
    ],
  },
  {
    key: "group.walker_funnel",
    label: "Walker Funnel",
    icon: Briefcase,
    pages: [
      { key: "page.walker_applications", label: "Walker-Bewerbungen", path: "/admin/walker-applications", icon: Briefcase, badgeKey: "walker_apps_new" },
      { key: "page.trial_slots", label: "Probetag Slot Zuweisung", path: "/admin/trial-slots", icon: CalendarDays },
    ],
  },
  {
    key: "group.operations",
    label: "Operations",
    icon: Settings,
    pages: [
      { key: "page.employees", label: "Mitarbeiter", path: "/admin/employees", icon: UserCog },
      { key: "page.shift_assignment", label: "Schicht Zuteilung", path: "/admin/shift-assignment", icon: CalendarDays, badgeKey: "schicht_aenderungen" },
      // M15-3 Zeit-Kalibrierung (Noah 05.11.2026): freiwillige Selbstaufschreibung 09.11. bis 20.11.2026, erbt group.operations
      // (alle Rollen außer Walker). Daten sieht jede Person nur selbst (RLS), Auswertung nur zusammengefasst.
      { key: "page.meine_zeit", label: "Meine Zeit", path: "/admin/meine-zeit", icon: Clock },
      {
        key: "page.customer_support",
        label: "Customer Support",
        path: "/admin/customer-support",
        icon: HeadphonesIcon,
        // CO-8 (Paul 23.10.2026): Bereiche und Postfach-Gruppen je Rolle in „Personen & Rechte“.
        bereiche: [
          {
            key: "page.customer_support.posteingang",
            label: "Posteingang",
            unter: [
              { key: "page.customer_support.postfach.kunden", label: "Kunden-Postfächer (Queue, Meine Queue, WhatsApp, App, Top of Funnel, Urlauber)" },
              { key: "page.customer_support.postfach.crew", label: "Crew" },
              { key: "page.customer_support.postfach.funnel_bottom", label: "Bottom of Funnel", hinweis: NUR_FOUNDER_ADMIN_DATEN },
            ],
          },
          { key: "page.customer_support.ki_agent", label: "KI-Agent (Kaul)", hinweis: NUR_FOUNDER_ADMIN_DATEN },
          { key: "page.customer_support.auswertung", label: "Auswertung" },
          {
            key: "page.customer_support.verwaltung",
            label: "Verwaltung",
            hinweis: "Löschanträge und Superchat-Autoren liefert die Datenbank heute nur Founder und Admin.",
          },
        ],
      },
    ],
  },
  {
    // Heutige Routen: eigener Link oben in der Leiste (dynamisch), hier nur Seitenschutz.
    key: "group.routen",
    label: "Routen",
    icon: Navigation,
    hidden: true,
    pages: [
      { key: "page.routes", label: "Routen", path: "/admin/routes", icon: Navigation },
    ],
  },
  {
    key: "group.kunden",
    label: "Kunden",
    icon: Users,
    pages: [
      { key: "page.customers", label: "Kunden", path: "/admin/customers", icon: Users, routes: ["customers", "customers/:customerId", "customers-plz"] },
      { key: "page.dogs", label: "Hunde", path: "/admin/dogs", icon: Dog, routes: ["dogs", "dogs/:dogId"] },
      { key: "page.dog_level_review", label: "Hunde-Level", path: "/admin/dog-level-review", icon: Star },
      { key: "page.customer_history", label: "Kunden History", path: "/admin/customer-history", icon: Activity },
      { key: "page.cancellations", label: "Stornierungen", path: "/admin/cancellations", icon: XCircle },
      { key: "page.dog_rename", label: "Hunde umbenennen", path: "/admin/dog-rename", icon: Dog },
      { key: "page.referrals", label: "Referrals", path: "/admin/referrals", icon: Gift },
      { key: "page.customer_status", label: "Statusübersicht", path: "/admin/customer-status", icon: UserCheck },
      { key: "page.specials", label: "Specials", path: "/admin/specials", icon: PartyPopper },
      // Aus dem Altbestand (Noah 02.10.2026). Eigene Rechte-Zeilen halten den bisherigen Zugriff (nur Founder/Admin).
      { key: "page.news", label: "News", path: "/admin/news", icon: Newspaper },
      { key: "page.customer_audit_log", label: "Kunden-Änderungsprotokoll", path: "/admin/customer-audit-log", icon: ClipboardList },
      { key: "page.feedback", label: "Feedback", path: "/admin/feedback", icon: MessageCircle },
    ],
  },
  {
    key: "group.stats",
    label: "Stats",
    icon: BarChart3,
    pages: [
      // Link unter Stats, die Route schützt page.daycare_abo_stats_route (Daycare), siehe oben.
      { key: "page.daycare_abo_stats", label: "Daycare-Abos", path: "/admin/stats/daycare-abos", icon: PawPrint, routes: [] },
      // Aus der Daycare-Gruppe hierher verschoben (Founder 11.09.2026): Abos sind Geld-/Vertragsdaten.
      { key: "page.daycare_abos", label: "Abo-Verwaltung", path: "/admin/daycare/abos", icon: Settings },
      { key: "page.payments", label: "Zahlungen", path: "/admin/payments", icon: Euro },
      { key: "page.passes", label: "Karten", path: "/admin/passes", icon: CreditCard },
      { key: "page.kyc", label: "Know Your Customer", path: "/admin/kyc", icon: Users },
      { key: "page.chat_analytics", label: "Chat Analytics", path: "/admin/chat-analytics", icon: MessageCircle },
    ],
  },
  {
    key: "group.admin",
    label: "Admin",
    icon: Shield,
    pages: [
      { key: "page.activity_log", label: "Aktivitäts-Log", path: "/admin/activity-log", icon: ClipboardList },
      { key: "page.sync_dashboard", label: "Sync-Dashboard", path: "/admin/sync-dashboard", icon: Activity },
      // Personen & Rechte (Teil 2) ersetzt die Einträge „Rollen“ und „Seiten-Zugriff“. Die alten Adressen
      // leiten dorthin um und behalten ihren Schlüssel, damit der Zugriff 1:1 bleibt.
      { key: "admin.page_access", label: "Personen & Rechte", path: "/admin/personen-rechte", icon: Shield, routes: ["personen-rechte", "page-access"] },
      { key: "admin.roles", label: "Rollen", path: "/admin/roles", icon: Shield, hidden: true },
      { key: "admin.locations", label: "Standorte", path: "/admin/locations", icon: Building2 },
      { key: "admin.kontrollzentrum", label: "Kontrollzentrum", path: "/admin/kontrollzentrum", icon: SlidersHorizontal },
      // Avatar-Zentrale (A3): seit 19.10.2026 (Noah) unter Kontrollzentrum statt Daycare, erbt group.admin.
      { key: "page.avatare", label: "Avatare", path: "/admin/avatare", icon: Sparkles },
      { key: "admin.user_management", label: "User-IDs reparieren", path: "/admin/fix-user-ids", icon: Shield, hidden: true },
    ],
  },
  {
    // Altbestand: nicht in der Leiste, Zugriff wie bisher admin.legacy (nur Founder/Admin).
    // 04.10.2026: 7 von 10 Seiten stillgelegt (Adressen führen auf /admin). Bleiben, weil noch gebraucht:
    // Kalender (Button „Kalender“ in Mein Tag, Walker-Freigabe), Kapazität (einzige Oberfläche für
    // Umland-Warteliste, L3-Plätze und Abholfenster-Limits), Kunden-Buchungen (einzige Pflege der
    // Verkaufsnotizen, 92 Profile). Vor einem Stilllegen erst Ersatz schaffen.
    key: "group.legacy",
    label: "Altbestand",
    icon: Archive,
    hidden: true,
    pages: [
      { key: "page.calendar", label: "Kalender", path: "/admin/calendar", icon: CalendarDays },
      { key: "page.capacity", label: "Kapazität (Gassi)", path: "/admin/capacity", icon: Gauge },
      { key: "page.customer_bookings", label: "Kunden-Buchungen", path: "/admin/customer-bookings", icon: Users },
    ],
  },
];

/** Routen (relativ zu /admin), die eine Seite schützt. */
export function pageRoutes(page: AdminNavPage): string[] {
  if (page.routes) return page.routes;
  if (!page.path.startsWith("/admin/")) return [];
  return [page.path.slice("/admin/".length)];
}
